'use client';

/**
 * First-purchase coupon popup. Shown once per store to logged-out shoppers when
 * the store has an active coupon flagged "offer on first purchase". The shopper
 * enters a phone number (no authentication); when it has never ordered at this
 * store, the coupon is auto-applied to the cart so it carries into checkout.
 */

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Tag } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { isAuthenticated } from '@/lib/auth';
import { useCartStore, type CartCoupon } from '@/lib/cartStore';
import { formatCurrency } from '@/lib/app-utils';

type ApiCoupon = {
  id: string;
  name: string;
  code: string;
  type: 'AMOUNT' | 'PERCENTAGE';
  amount: number | string;
  appliesTo: CartCoupon['appliesTo'];
  productId: string | null;
  product: { id: string; name: string } | null;
};

type FirstPurchaseCouponPopupProps = {
  storeId: string;
  normalizedLocale: string;
};

function dismissedKey(storeId: string): string {
  return `first-purchase-coupon-dismissed:${storeId}`;
}

function toCartCoupon(coupon: ApiCoupon): CartCoupon {
  return {
    id: coupon.id,
    code: coupon.code,
    type: coupon.type,
    appliesTo: coupon.appliesTo,
    amount: Number(coupon.amount),
    productId: coupon.productId,
    productName: coupon.product?.name ?? null,
  };
}

export function FirstPurchaseCouponPopup({ storeId, normalizedLocale }: FirstPurchaseCouponPopupProps) {
  const t = useTranslations('firstPurchaseCoupon');
  const setCoupon = useCartStore((state) => state.setCoupon);

  const [coupon, setCouponData] = useState<ApiCoupon | null>(null);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applied, setApplied] = useState(false);
  const [notEligible, setNotEligible] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) return;
    if (localStorage.getItem(dismissedKey(storeId))) return;

    let cancelled = false;
    fetch(`/api/stores/${storeId}/first-purchase-coupon`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { coupon: ApiCoupon | null } | null) => {
        if (cancelled || !data?.coupon) return;
        setCouponData(data.coupon);
        setOpen(true);
      })
      .catch(() => {
        /* offline / transient — simply don't show the popup */
      });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const markDismissed = () => {
    try {
      localStorage.setItem(dismissedKey(storeId), '1');
    } catch {
      /* localStorage unavailable — best effort */
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) markDismissed();
  };

  const handleSubmit = async () => {
    if (!coupon || !phone.trim() || submitting) return;
    setSubmitting(true);
    setNotEligible(false);
    try {
      const res = await fetch(`/api/stores/${storeId}/first-purchase-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), locale: normalizedLocale }),
      });
      const data = (await res.json().catch(() => null)) as
        | { eligible: boolean; coupon: ApiCoupon | null }
        | null;

      if (res.ok && data?.eligible && data.coupon) {
        setCoupon(toCartCoupon(data.coupon));
        setApplied(true);
        markDismissed();
      } else {
        setNotEligible(true);
        markDismissed();
      }
    } catch {
      setNotEligible(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!coupon) return null;

  const discountLabel =
    coupon.type === 'PERCENTAGE'
      ? `${Number(coupon.amount)}%`
      : formatCurrency(Number(coupon.amount), normalizedLocale);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Tag className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">
            {applied ? t('appliedTitle') : t('title', { discount: discountLabel })}
          </DialogTitle>
          <DialogDescription className="text-center">
            {applied
              ? t('appliedDescription', { code: coupon.code })
              : notEligible
                ? t('notEligible')
                : t('description')}
          </DialogDescription>
        </DialogHeader>

        {!applied && !notEligible && (
          <div className="grid gap-2 py-2">
            <Label htmlFor="first-purchase-phone">{t('phoneLabel')}</Label>
            <Input
              id="first-purchase-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t('phonePlaceholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              disabled={submitting}
            />
          </div>
        )}

        <DialogFooter>
          {applied || notEligible ? (
            <Button className="w-full" onClick={() => handleOpenChange(false)}>
              {t('close')}
            </Button>
          ) : (
            <Button className="w-full" onClick={handleSubmit} disabled={submitting || !phone.trim()}>
              {submitting ? <Spinner className="mr-2 size-3" /> : null}
              {t('submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
